// Cliente único de Edge Functions. Unifica la invocación (URL, auth, parseo de
// error) y elimina las dos formas incompatibles que convivían: supabase.functions.invoke
// y fetch crudo a `${VITE_SUPABASE_URL}/functions/v1/...`.
import { supabase } from "../../supabase";
import { extractEdgeFunctionError } from "../edgeError";

const FUNCTIONS_URL = import.meta.env.VITE_SUPABASE_URL + "/functions/v1";
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Invoca una Edge Function usando la sesión ambiente (supabase-js adjunta el token).
 * Lanza `Error` con el mensaje extraído si falla, o si la función devuelve `{ error }`
 * en un 200. Devuelve el `data` tipado.
 */
export async function invoke<T = unknown>(
  name: string,
  body?: Record<string, unknown>,
  opts?: { fallback?: string }
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, body ? { body } : undefined);
  if (error) {
    throw new Error(await extractEdgeFunctionError(error, opts?.fallback ?? `Error en ${name}`));
  }
  if (data && typeof data === "object" && "error" in data && (data as { error?: unknown }).error) {
    throw new Error(String((data as { error: unknown }).error));
  }
  return data as T;
}

/**
 * POST directo a una Edge Function vía fetch. Para llamadas que necesitan un token
 * explícito (p.ej. Stripe) o que se hacen sin sesión ambiente (páginas públicas).
 * Siempre envía la anon key como `apikey` (requerida por el gateway).
 */
export async function postToFunction<T = unknown>(
  name: string,
  opts: { body?: unknown; token?: string; fallback?: string } = {}
): Promise<T> {
  const res = await fetch(`${FUNCTIONS_URL}/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: ANON_KEY,
      ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string })?.error ?? opts.fallback ?? `Error en ${name}`);
  }
  return data as T;
}
